import {useContext} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import AtomBadge, {atomBadgeDesigns, atomBadgeSizes} from '@s-ui/react-atom-badge'
import AtomButton, {atomButtonDesigns} from '@s-ui/react-atom-button'
import {atomIconColors, atomIconRenders, atomIconSizes} from '@s-ui/react-atom-icon'
import Context from '@s-ui/react-context'
import IconArrowRightMedium from '@adv-mt/mt-svg-icons/lib/ArrowRightMedium'
import IconCarEngineOutline from '@adv-mt/mt-svg-icons/lib/CarEngineOutline'
import IconFuelDiesel from '@adv-mt/mt-svg-icons/lib/Diesel'
import IconDoorsOutline from '@adv-mt/mt-svg-icons/lib/DoorsOutline'
import IconEditOutline from '@adv-mt/mt-svg-icons/lib/EditOutline'
import IconFuelStation from '@adv-mt/mt-svg-icons/lib/FuelOutline'
import IconFuelGasoline from '@adv-mt/mt-svg-icons/lib/Gasolina'
import IconGearAuto from '@adv-mt/mt-svg-icons/lib/GearAuto'
import IconGearManual from '@adv-mt/mt-svg-icons/lib/GearManual'
import IconKm from '@adv-mt/mt-svg-icons/lib/Km'
import IconPlate from '@adv-mt/mt-svg-icons/lib/Plate'
import TitleBasic, {titleBasicColors, titleBasicSizes, titleBasicTags} from '@adv-mt/mt-title-basic'

const BASE_CLASS_NAME = 'mt-Landing-InstantOffer-StepVehicleSelect-CardVehicleSpecs'

const CardVehicleSpecs = ({areEditableSpecs = false, onClickCard, onClickEditData, title, vehicleData = {}}) => {
  const {domain, i18n} = useContext(Context)
  const config = domain.get('config')
  const isMobile = config.get('isMobile')
  const isDesktop = !isMobile
  const {FUEL_TYPE_DIESEL_ID, FUEL_TYPE_GASOLINE_ID, TRANNSMISSION_TYPE_ID_AUTOMATIC, TRANNSMISSION_TYPE_ID_MANUAL} =
    config.get('search')

  const isCardClickable = Boolean(onClickCard)

  const {
    bodyType,
    doors,
    fuelType,
    fuelTypeId,
    km,
    plate,
    plateRegistrationYear,
    transmissionType,
    transmissionTypeId,
    version
  } = vehicleData

  const getTypeIconFuel = fuelId => {
    const fuelTypesIconList = {
      [FUEL_TYPE_DIESEL_ID]: IconFuelDiesel,
      [FUEL_TYPE_GASOLINE_ID]: IconFuelGasoline
    }
    return fuelTypesIconList[fuelId] || IconFuelStation
  }

  const getTypeIconTransmission = transmissionId => {
    const transmissionTypesIconList = {
      [TRANNSMISSION_TYPE_ID_AUTOMATIC]: IconGearAuto,
      [TRANNSMISSION_TYPE_ID_MANUAL]: IconGearManual
    }
    return transmissionTypesIconList[transmissionId] || IconGearManual
  }

  const vehicleSpecs = []

  if (plateRegistrationYear)
    vehicleSpecs.push({
      Icon: IconPlate,
      value: plateRegistrationYear
    })

  if (bodyType && doors)
    vehicleSpecs.push({
      Icon: IconDoorsOutline,
      value: i18n.t('TECHNICAL_DETAILS_BODY_TYPE_DOORS_TAG', {bodyType, doors})
    })

  if (fuelType && fuelTypeId)
    vehicleSpecs.push({
      Icon: getTypeIconFuel(fuelTypeId),
      value: fuelType
    })

  if (transmissionType && transmissionTypeId)
    vehicleSpecs.push({
      Icon: getTypeIconTransmission(transmissionTypeId),
      value: transmissionType
    })

  if (km)
    vehicleSpecs.push({
      Icon: IconKm,
      value: i18n.n(Number(km))
    })

  const baseClassName = cx(BASE_CLASS_NAME, {
    [`${BASE_CLASS_NAME} ${BASE_CLASS_NAME}--isClickable`]: isCardClickable,
    [`${BASE_CLASS_NAME} ${BASE_CLASS_NAME}--isMobile`]: areEditableSpecs && isMobile
  })
  const versionClassName = cx(`${BASE_CLASS_NAME}-specsVersion`, {
    [`${BASE_CLASS_NAME}-specsVersion--highlight`]: areEditableSpecs,
    [`${BASE_CLASS_NAME}-specsVersion--highlight--isDesktop`]: areEditableSpecs && isDesktop
  })

  const handleClickEditData = () => {
    onClickEditData()
  }

  return (
    <div className={baseClassName} {...(isCardClickable && {onClick: () => onClickCard()})}>
      <div>
        <div className={`${BASE_CLASS_NAME}-titleWrapper`}>
          <div className={`${BASE_CLASS_NAME}-title`}>
            <TitleBasic
              as={titleBasicTags.h2}
              color={titleBasicColors.currentColor}
              size={titleBasicSizes.xs}
              title={title}
            />
            {plate ? (
              <AtomBadge design={atomBadgeDesigns.SOFT} label={plate} size={atomBadgeSizes.LARGE} type={'primary'} />
            ) : null}
          </div>
          {areEditableSpecs && isDesktop && (
            <AtomButton
              design={atomButtonDesigns.LINK}
              leftIcon={
                <div className={`${BASE_CLASS_NAME}-editIcon`}>
                  <IconEditOutline render={atomIconRenders.lazy} size={atomIconSizes.medium} />
                </div>
              }
              onClick={() => handleClickEditData()}
            >
              {'Editar datos'}
            </AtomButton>
          )}
        </div>
        {version ? (
          <div className={versionClassName}>
            <span className={`${BASE_CLASS_NAME}-specsIcon ${BASE_CLASS_NAME}-specsIconVersion`}>
              <IconCarEngineOutline render={atomIconRenders.lazy} size={atomIconSizes.small} />
            </span>
            {version}
          </div>
        ) : null}
        {vehicleSpecs.length ? (
          <div className={`${BASE_CLASS_NAME}-specs`}>
            <ul className={`${BASE_CLASS_NAME}-specsList`}>
              {vehicleSpecs.map(({value, Icon}, index) => (
                <li key={index} className={`${BASE_CLASS_NAME}-specsItem`}>
                  {Icon ? (
                    <span className={`${BASE_CLASS_NAME}-specsIcon`}>
                      <Icon render={atomIconRenders.lazy} size={atomIconSizes.small} />
                    </span>
                  ) : null}
                  {value}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
      {isCardClickable && (
        <div className={`${BASE_CLASS_NAME}-nextIcon`}>
          <IconArrowRightMedium
            color={atomIconColors.primary}
            render={atomIconRenders.lazy}
            size={atomIconSizes.medium}
          />
        </div>
      )}
      {areEditableSpecs && isMobile && (
        <div className={`${BASE_CLASS_NAME}-nextButton`}>
          <AtomButton
            design={atomButtonDesigns.LINK}
            leftIcon={
              <div className={`${BASE_CLASS_NAME}-editIcon`}>
                <IconEditOutline render={atomIconRenders.lazy} size={atomIconSizes.medium} />
              </div>
            }
            onClick={() => handleClickEditData()}
          >
            {'Editar datos'}
          </AtomButton>
        </div>
      )}
    </div>
  )
}

CardVehicleSpecs.displayName = 'CardVehicleSpecs'

CardVehicleSpecs.propTypes = {
  areEditableSpecs: PropTypes.bool,
  onClickCard: PropTypes.func,
  onClickEditData: PropTypes.func,
  title: PropTypes.string.isRequired,
  vehicleData: PropTypes.object.isRequired
}

export default CardVehicleSpecs
